// Date the stock already on the shelf (F-CTRL-N73). Turning "Track expiry" on
// changes nothing about stock already held, so those lots stay undated — and
// out of every expiry alert — until somebody puts a date on them. This lists
// them all in one place, with a suggestion wherever the item has a shelf life.
import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import type { UndatedLot } from '@/types'
import { Button } from '@/components/ui/button'
import { DatePicker } from '@/components/ui/date-picker'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { formatDateHuman } from '@/lib/utils'
import { fmtQty } from '@/lib/quantity'
import { LOT_CHANGE_QUERY_KEYS } from './WriteOffLotDialog'

export const UNDATED_LOTS_QK = ['undatedLots'] as const

export function useUndatedLots(enabled = true) {
  return useQuery({
    queryKey: UNDATED_LOTS_QK,
    queryFn: () => apiClient.getUndatedLots().then((r) => r.data ?? []),
    enabled,
    staleTime: 30_000,
  })
}

/** Rows ready to save: a lot with a date, as the API wants them. */
export function datedRows(dates: Record<string, string>): { id: string; expiry_date: string }[] {
  return Object.entries(dates)
    .filter(([, d]) => d.trim() !== '')
    .map(([id, d]) => ({ id, expiry_date: d.trim() }))
}

export function DateStockDialog({ open, onClose, onlyItemId, showToast }: {
  open: boolean
  onClose: () => void
  /** Limit the list to one item (opened from that item's Edit form). */
  onlyItemId?: string
  showToast: (type: 'success' | 'error', message: string) => void
}) {
  const qc = useQueryClient()
  const { data = [], isLoading } = useUndatedLots(open)
  const lots: UndatedLot[] = useMemo(
    () => (onlyItemId ? data.filter((l) => l.stock_item_id === onlyItemId) : data),
    [data, onlyItemId],
  )
  const [dates, setDates] = useState<Record<string, string>>({})
  useEffect(() => {
    if (open) setDates({})
  }, [open])

  const withSuggestion = lots.filter((l) => l.suggested_expiry)
  const rows = datedRows(dates)

  const save = useMutation({
    mutationFn: () => apiClient.dateStockLots(rows),
    onSuccess: (res) => {
      for (const key of LOT_CHANGE_QUERY_KEYS) qc.invalidateQueries({ queryKey: [key] })
      qc.invalidateQueries({ queryKey: UNDATED_LOTS_QK })
      showToast('success', `Dated ${res.data?.dated ?? rows.length} lot${rows.length === 1 ? '' : 's'}. They now show in your expiry alerts.`)
      onClose()
    },
    onError: (e: Error) => showToast('error', e.message || 'Could not save the dates'),
  })

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !save.isPending && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Date your stock</DialogTitle>
          <DialogDescription>
            These items are tracked for expiry, but some of the stock you already had came in without a date.
            Check the packs and set a date on each. Anything left blank stays as it is.
          </DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <p className="py-6 text-sm text-muted-foreground">Loading…</p>
        ) : lots.length === 0 ? (
          <p className="rounded-lg border border-dashed bg-muted/20 p-4 text-sm text-muted-foreground">
            Nothing to date. Every lot of your tracked items has an expiry date.
          </p>
        ) : (
          <div className="space-y-3">
            {withSuggestion.length > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-muted/40 px-3 py-2 text-sm">
                <span className="text-muted-foreground">
                  {withSuggestion.length} lot{withSuggestion.length === 1 ? ' has' : 's have'} a suggested date: the day it arrived plus the item&apos;s shelf life.
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setDates((d) => {
                    const next = { ...d }
                    for (const l of withSuggestion) if (!next[l.batch_id]) next[l.batch_id] = l.suggested_expiry!
                    return next
                  })}
                >
                  Use suggested dates
                </Button>
              </div>
            )}
            <ul className="divide-y rounded-lg border">
              {lots.map((l) => (
                <li key={l.batch_id} className="flex flex-wrap items-center justify-between gap-3 px-3 py-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{l.item_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {fmtQty(l.quantity_remaining)} {l.unit} · received {formatDateHuman(l.received_at)}
                      {l.lot_no ? ` · lot ${l.lot_no}` : ''}
                      {l.location_name ? ` · ${l.location_name}` : ''}
                      {l.suggested_expiry ? ` · suggested ${formatDateHuman(l.suggested_expiry)}` : ''}
                    </p>
                  </div>
                  <DatePicker
                    className="w-48"
                    value={dates[l.batch_id] ?? ''}
                    onChange={(v) => setDates((d) => ({ ...d, [l.batch_id]: v }))}
                    placeholder="Expiry date"
                  />
                </li>
              ))}
            </ul>
          </div>
        )}
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={onClose} disabled={save.isPending}>Cancel</Button>
          <Button disabled={rows.length === 0 || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : rows.length > 0 ? `Save ${rows.length} date${rows.length === 1 ? '' : 's'}` : 'Save dates'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
